import { createStoredSignal } from "@utils/createStoredSignal";
import { createVisiblePoll } from "@utils/createVisiblePoll";
import { keepIfSameJson } from "@utils/keepIfSameJson";
import {
	createEffect,
	createResource,
	createRoot,
	createSignal,
} from "solid-js";
import { getConnectedAccountsStats } from "@/client/sdk.gen";
import type { InfluencerConnectedAccountsStatsResponse } from "@/client/types.gen";
import { useWebSocket } from "~/modules/shared/utils/useWebSocket";

const FALLBACK_POLL_MS = 60_000;
const STATS_STORAGE_KEY = "connected-accounts-stats";

declare global {
	interface Window {
		pageVars?: {
			userInfluencerId?: string;
			/** The signed-in user's id, for their own realtime channel. */
			activityUserId?: number;
		};
	}
}

type UseConnectedAccountsStatsOptions = {
	/** Refetch on an interval while WebSocket pushes are not arriving. */
	fallbackPoll?: boolean;
};

async function fetchConnectedAccountsStats(): Promise<
	InfluencerConnectedAccountsStatsResponse | undefined
> {
	const response = await getConnectedAccountsStats();
	return response.data;
}

function statsStorageKey() {
	if (typeof window === "undefined") {
		return STATS_STORAGE_KEY;
	}
	const influencerId = window.pageVars?.userInfluencerId;
	return influencerId
		? `${STATS_STORAGE_KEY}:${influencerId}`
		: STATS_STORAGE_KEY;
}

function createConnectedAccountsStats(
	options: UseConnectedAccountsStatsOptions,
) {
	const fallbackPoll = options.fallbackPoll ?? true;
	const [cached, setCached] = createStoredSignal<
		InfluencerConnectedAccountsStatsResponse | undefined
	>(statsStorageKey(), undefined, { storage: "session" });

	let latest: InfluencerConnectedAccountsStatsResponse | undefined = cached();
	const [stats, { refetch, mutate }] = createResource(
		async () => {
			const data = await fetchConnectedAccountsStats();
			const next = keepIfSameJson(latest, data);
			if (next && next !== latest) {
				setCached(next);
			}
			latest = next;
			return next;
		},
		{ initialValue: cached() },
	);

	const [catchUpQueued, setCatchUpQueued] = createSignal(false);
	const influencerPublicId = window.pageVars?.userInfluencerId;
	const { isLive: hasRealtimePush } =
		useWebSocket<InfluencerConnectedAccountsStatsResponse>({
			channel: influencerPublicId
				? `influencers.${influencerPublicId}`
				: undefined,
			event: ".connected-accounts-stats.updated",
			onMessage: (payload) => {
				const next = keepIfSameJson(latest, payload);
				if (next === latest) {
					return;
				}
				latest = next;
				setCached(next);
				mutate(next);
			},
			onLive: () => setCatchUpQueued(true),
		});

	// A fetch already in flight may have read the server before the channel
	// subscribed, so the catch-up waits for it rather than being skipped.
	createEffect(() => {
		if (catchUpQueued() && !stats.loading) {
			setCatchUpQueued(false);
			void refetch();
		}
	});

	if (fallbackPoll) {
		createVisiblePoll({
			enabled: () => !hasRealtimePush(),
			refetch,
			loading: () => stats.loading,
			intervalMs: FALLBACK_POLL_MS,
		});
	}

	const mutateStats = (
		updater: (
			prev: InfluencerConnectedAccountsStatsResponse | undefined,
		) => InfluencerConnectedAccountsStatsResponse | undefined,
	) => {
		const next = keepIfSameJson(latest, updater(latest));
		if (next && next !== latest) {
			setCached(next);
		}
		latest = next;
		mutate(next);
	};

	return { stats, refetch, mutate: mutateStats, hasRealtimePush };
}

let shared: ReturnType<typeof createConnectedAccountsStats> | undefined;

/**
 * Shared across every caller on the page (sub-nav, stats cards, prompts).
 * Each full page load still fetches once; session storage covers the first paint.
 */
export function useConnectedAccountsStats(
	options: UseConnectedAccountsStatsOptions = {},
) {
	if (!shared) {
		shared = createRoot(() => createConnectedAccountsStats(options));
	}
	return shared;
}
